import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, ChevronLeft, ChevronRight, HelpCircle, Shield, Clock, Gauge, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  {
    id: "risk",
    title: "Profil de risque",
    subtitle: "Quel type d'investisseur êtes-vous ?",
    icon: Gauge,
    options: [
      { value: "prudent", label: "Prudent", description: "Je préfère limiter les pertes" },
      { value: "equilibre", label: "Équilibré", description: "J'accepte des fluctuations modérées" },
      { value: "dynamique", label: "Dynamique", description: "J'accepte plus de risque pour plus de rendement" }
    ]
  },
  {
    id: "horizon",
    title: "Horizon d'investissement",
    subtitle: "Quel est votre horizon d'investissement ?",
    icon: Clock,
    options: [
      { value: "court", label: "Court terme", description: "Moins de 3 ans" },
      { value: "moyen", label: "Moyen terme", description: "3 à 10 ans" },
      { value: "long", label: "Long terme", description: "Plus de 10 ans" }
    ]
  },
  {
    id: "detail",
    title: "Niveau de détail",
    subtitle: "Quel niveau de détail souhaitez-vous ?",
    icon: TrendingUp,
    options: [
      { value: "essentiel", label: "Essentiel", description: "Résumé clair, sans jargon" },
      { value: "complet", label: "Complet", description: "Données détaillées, pourcentages" }
    ]
  },
  {
    id: "sector",
    title: "Secteur d'emploi",
    subtitle: "Dans quel secteur travaillez-vous ?",
    icon: Briefcase,
    hasTooltip: true,
    tooltipContent: "Si vous travaillez dans la Tech et investissez beaucoup en Tech, vous subiriez une double perte en cas de crise du secteur : votre emploi ET votre portefeuille seraient touchés en même temps. Nous limitons automatiquement votre exposition à votre secteur d'emploi à 15% maximum pour vous protéger de ce risque.",
    options: [
      { value: "tech", label: "Tech / Software", description: "" },
      { value: "finance", label: "Finance / Banque", description: "" },
      { value: "sante", label: "Santé / Pharma", description: "" },
      { value: "energie", label: "Énergie", description: "" },
      { value: "industrie", label: "Industrie", description: "" },
      { value: "commerce", label: "Commerce / Retail", description: "" },
      { value: null, label: "Autre / Non concerné", description: "" }
    ]
  }
];

export default function OnboardingPage({ user, setUser }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    risk_profile: null,
    horizon: null,
    detail_level: null,
    employment_sector: undefined
  });
  const [loading, setLoading] = useState(false);

  const step = STEPS[currentStep];
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const canProceed = () => {
    switch (currentStep) {
      case 0: return answers.risk_profile !== null;
      case 1: return answers.horizon !== null;
      case 2: return answers.detail_level !== null;
      case 3: return answers.employment_sector !== undefined;
      default: return false;
    }
  };

  const handleSelect = (value) => {
    switch (currentStep) {
      case 0:
        setAnswers({ ...answers, risk_profile: value });
        break;
      case 1:
        setAnswers({ ...answers, horizon: value });
        break;
      case 2:
        setAnswers({ ...answers, detail_level: value });
        break;
      case 3:
        setAnswers({ ...answers, employment_sector: value });
        break;
    }
  };

  const getCurrentValue = () => {
    switch (currentStep) {
      case 0: return answers.risk_profile;
      case 1: return answers.horizon;
      case 2: return answers.detail_level;
      case 3: return answers.employment_sector;
      default: return null;
    }
  };

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Submit onboarding
      setLoading(true);
      try {
        await axios.post(`${API}/onboarding`, answers);
        toast.success("Profil configuré avec succès !");
        
        // Fetch updated user
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        
        navigate("/dashboard");
      } catch (error) {
        console.error("Onboarding error:", error);
        toast.error("Erreur lors de la configuration du profil");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const StepIcon = step.icon;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold tracking-tight font-manrope">Oracle</span>
          </div>
          <span className="text-sm text-muted-foreground">
            Étape {currentStep + 1} sur {STEPS.length}
          </span>
        </div>
      </header>

      {/* Progress bar */}
      <div className="max-w-2xl mx-auto w-full px-4 pt-6">
        <Progress value={progress} className="h-1" />
      </div>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg animate-fade-in" key={currentStep}>
          {/* Step header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-sm bg-card border border-border flex items-center justify-center mx-auto mb-6">
              <StepIcon className="w-8 h-8 text-primary" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <h1 className="text-2xl font-bold tracking-tight font-manrope">{step.title}</h1>
              {step.hasTooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button data-testid="sector-help-btn" className="text-muted-foreground hover:text-foreground transition-colors">
                        <HelpCircle className="w-5 h-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm p-4">
                      <div className="flex items-start gap-2">
                        <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold mb-1">Pourquoi cette question ?</p>
                          <p className="text-sm text-muted-foreground">{step.tooltipContent}</p>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-muted-foreground">{step.subtitle}</p>
          </div>

          {/* Options */}
          <div className="space-y-3 mb-8">
            {step.options.map((option) => (
              <button
                key={option.value ?? 'none'}
                data-testid={`option-${option.value ?? 'other'}`}
                onClick={() => handleSelect(option.value)}
                className={`w-full p-4 rounded-sm border text-left transition-all card-hover ${
                  getCurrentValue() === option.value
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card hover:border-primary/30"
                }`}
              >
                <div className="font-medium">{option.label}</div>
                {option.description && (
                  <div className="text-sm text-muted-foreground mt-1">{option.description}</div>
                )}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-btn"
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </Button>
            <Button
              data-testid="next-btn"
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="gap-2 rounded-sm"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {currentStep === STEPS.length - 1 ? "Terminer" : "Continuer"}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
